/**
 * Muxing Video Exporter (Soft Export)
 *
 * Export ultra-rapide utilisant UNIQUEMENT FFmpeg avec -c copy (pas de ré-encodage)
 *
 * AVANTAGES :
 * - Ultra rapide : pas de ré-encodage, juste du muxing
 * - Qualité préservée : copie exacte des flux vidéo/audio
 * - Pas de crash mémoire : FFmpeg gère tout
 *
 * LIMITATIONS :
 * - Pas d'overlays (intro/outro/text/images) - ils sont ignorés
 * - Pas de sous-titres en burn (uniquement soft subtitles en piste séparée)
 * - Tous les clips doivent être compatibles pour concat
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { Clip, Track, SubtitleStyle } from './types';
import { ExportConfig, ProgressCallback, ExportProgress, ASPECT_RATIOS } from './webcodecs-exporter';
import { FFmpegOutOfMemoryError, isLikelyFFmpegOutOfMemoryError } from './ffmpeg-errors';
import { FFmpegService } from './ffmpeg-service';


export type MuxingExportConfig = ExportConfig;

export class MuxingExporter {
  private config: MuxingExportConfig;
  private progressCallback: ProgressCallback;
  private ffmpeg: FFmpeg | null = null;
  private isFFmpegLoaded = false;
  private aborted = false;
  private sourceCache = new Map<string, string>();
  private sourceSequence = 0;
  private ffmpegLogs: string[] = [];

  constructor(config: MuxingExportConfig, progressCallback: ProgressCallback) {
    this.config = config;
    this.progressCallback = progressCallback;
  }

  /**
   * Abort export
   */
  abort(): void {
    this.aborted = true;
  }

  /**
   * Main export function - Soft export (muxing only, no re-encoding)
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    try {
      this.updateProgress({
        phase: 'preparing',
        progress: 0,
        message: 'Mode Soft Export : muxing rapide sans ré-encodage...'
      });

      // Filtrer les clips vidéo visibles (ignorer overlays)
      const videoClips = this.getVideoClips(clips, tracks);

      if (videoClips.length === 0) {
        throw new Error('Aucun clip vidéo à exporter. Le mode soft export nécessite des clips vidéo.');
      }

      this.updateProgress({
        phase: 'preparing',
        progress: 5,
        message: `${videoClips.length} clips vidéo détectés (overlays ignorés en mode soft)`
      });

      // Charger FFmpeg
      await this.loadFFmpeg();
      this.updateProgress({ phase: 'preparing', progress: 15, message: 'FFmpeg chargé' });

      // Créer la vidéo avec concat -c copy (ultra rapide)
      const baseVideoBlob = await this.createMuxedVideo(videoClips);

      // Vérifier s'il y a des sous-titres à ajouter
      const hasSubtitles = this.checkForSubtitles(clips, activeSubtitleLanguage);

      if (hasSubtitles) {
        this.updateProgress({
          phase: 'muxing',
          progress: 80,
          message: 'Ajout des sous-titres en piste séparée...'
        });

        // Collecter tous les sous-titres des clips vidéo utilisés
        // CRUCIAL: Calculer les offsets vidéo-relatifs car la vidéo finale n'a pas les gaps de la timeline
        const allSubtitles: any[] = [];
        let currentVideoOffset = 0;

        // videoClips est déjà trié par start
        for (const clip of videoClips) {
          if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
            const clipSubtitles = clip.subtitles[activeSubtitleLanguage];
            for (const sub of clipSubtitles) {
              const timelinePos = sub.start - clip.start;
              allSubtitles.push({
                ...sub,
                start: currentVideoOffset + timelinePos,
                end: currentVideoOffset + (sub.end - clip.start)
              });
            }
          }
          currentVideoOffset += clip.duration;
        }

        const finalBlob = await this.addSubtitlesWithFFmpeg(
          baseVideoBlob,
          allSubtitles, // Passer les sous-titres déjà offsetés
          activeSubtitleLanguage
        );

        this.updateProgress({ phase: 'complete', progress: 100, message: 'Export soft terminé !' });
        return finalBlob;
      }

      this.updateProgress({ phase: 'complete', progress: 100, message: 'Export soft terminé !' });
      return baseVideoBlob;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      this.updateProgress({
        phase: 'error',
        progress: 0,
        message: `Erreur: ${errorMessage}`
      });
      throw error;
    } finally {
      await this.cleanup();
    }
  }

  /**
   * Récupérer les clips vidéo visibles (PAS intro/outro/text/images)
   */
  private getVideoClips(clips: Clip[], tracks: Track[]): Clip[] {
    return clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      if (!track?.isVisible) return false;

      // Uniquement les clips vidéo normaux (pas intro/outro)
      return clip.type === 'video' &&
        clip.url &&
        clip.clipRole !== 'intro' &&
        clip.clipRole !== 'outro';
    }).sort((a, b) => a.start - b.start);
  }

  /**
   * Vérifier si les clips contiennent des sous-titres
   */
  private checkForSubtitles(clips: Clip[], activeSubtitleLanguage: string): boolean {
    return clips.some(clip =>
      clip.subtitles &&
      clip.subtitles[activeSubtitleLanguage] &&
      clip.subtitles[activeSubtitleLanguage].length > 0
    );
  }

  /**
   * Charger FFmpeg (instance partagée)
   */
  private async loadFFmpeg(): Promise<void> {
    if (this.isFFmpegLoaded && this.ffmpeg) return;

    console.log('[MuxingExporter] Récupération de l\'instance FFmpeg partagée...');

    this.ffmpeg = await FFmpegService.getInstance();
    this.ffmpegLogs = [];

    this.ffmpeg.on('log', ({ message }) => {
      console.log('[FFmpeg]', message);
      this.recordFFmpegLog(message);
    });

    this.ffmpeg.on('progress', ({ progress }) => {
      // Update progress based on FFmpeg progress
      const currentProgress = 15 + Math.floor(progress * 60); // 15-75%
      this.updateProgress({
        phase: 'muxing',
        progress: Math.min(currentProgress, 75),
        message: `Muxing : ${Math.round(progress * 100)}%`
      });
    });

    this.isFFmpegLoaded = true;
    console.log('[MuxingExporter] FFmpeg prêt ✓');
  }

  /**
   * Créer la vidéo muxée avec FFmpeg concat -c copy
   */
  private async createMuxedVideo(videoClips: Clip[]): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    const totalClips = videoClips.length;
    const manifestEntries: string[] = [];
    const usedSourceFiles = new Set<string>();

    this.updateProgress({
      phase: 'muxing',
      progress: 20,
      message: `Préparation de ${totalClips} clips pour muxing...`
    });

    for (let i = 0; i < totalClips; i++) {
      if (this.aborted) throw new Error('Export annulé');

      const clip = videoClips[i];
      const progress = 20 + Math.floor((i / totalClips) * 30); // 20-50%

      this.updateProgress({
        phase: 'muxing',
        progress,
        message: `Préparation clip ${i + 1}/${totalClips}...`
      });

      // OPTIMIZATION: Removed 2GB input guard to allow "concat cut" of large files.
      // We assume the verified output will be smaller than 2GB (browser limit).

      const sourceFilename = await this.ensureSourceFile(clip);
      usedSourceFiles.add(sourceFilename);

      const trimStart = clip.trimStart || 0;
      const inpoint = Math.max(0, trimStart);
      const outpoint = inpoint + clip.duration;

      console.log(`[MuxingExporter] Clip ${i}: ${clip.name}, trim=${trimStart}s, duration=${clip.duration}s`);

      manifestEntries.push(
        `file '${sourceFilename}'`,
        `inpoint ${inpoint.toFixed(6)}`,
        `outpoint ${outpoint.toFixed(6)}`,
        ''
      );
    }

    this.updateProgress({
      phase: 'muxing',
      progress: 55,
      message: 'Muxing des clips avec FFmpeg (ultra rapide)...'
    });

    // Créer le fichier concat manifest
    await this.ffmpeg.writeFile(
      'concat.txt',
      new TextEncoder().encode(manifestEntries.join('\n'))
    );

    console.log('[MuxingExporter] Concat manifest créé, lancement du muxing...');

    // Exécuter le concat avec -c copy (PAS de ré-encodage!)
    const concatArgs = [
      '-fflags', '+genpts', // Régénérer les timestamps
      '-f', 'concat',
      '-safe', '0',
      '-i', 'concat.txt',
      '-c', 'copy', // CRUCIAL: copie sans ré-encodage
      '-avoid_negative_ts', 'make_zero',
    ];

    try {
      // Essayer avec filtre AAC d'abord
      await this.execFFmpeg([
        ...concatArgs,
        '-bsf:a', 'aac_adtstoasc',
        'output.mp4'
      ], 'muxing avec filtre AAC');
    } catch (error) {
      if (error instanceof FFmpegOutOfMemoryError) {
        throw error;
      }

      console.warn('[MuxingExporter] Muxing avec filtre AAC échoué, tentative sans filtre...');
      await this.execFFmpeg([
        ...concatArgs,
        'output.mp4'
      ], 'muxing simple');
    }

    this.updateProgress({
      phase: 'muxing',
      progress: 75,
      message: 'Lecture du fichier muxé...'
    });

    try {
      const outputData = await this.ffmpeg.readFile('output.mp4');
      const outputBlob = new Blob([outputData as any], { type: 'video/mp4' });

      console.log('[MuxingExporter] Vidéo muxée créée:', outputBlob.size, 'bytes');

      return outputBlob;
    } finally {
      await this.safeDelete('concat.txt');
      await this.safeDelete('output.mp4');
      await this.purgeSources(usedSourceFiles);
    }
  }

  /**
   * Ajouter les sous-titres comme piste séparée (soft subtitles)
   */
  private async addSubtitlesWithFFmpeg(
    baseVideoBlob: Blob,
    allSubtitles: any[],
    activeSubtitleLanguage: string
  ): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    if (allSubtitles.length === 0) {
      return baseVideoBlob;
    }

    // Générer le fichier SRT (déjà triés par l'appelant)
    const srtContent = this.generateSRT(allSubtitles.sort((a, b) => a.start - b.start));

    await this.ffmpeg.writeFile('base_video.mp4', new Uint8Array(await baseVideoBlob.arrayBuffer()));
    await this.ffmpeg.writeFile('subtitles.srt', new TextEncoder().encode(srtContent));

    this.updateProgress({
      phase: 'muxing',
      progress: 90,
      message: 'Muxing des sous-titres (sans ré-encodage)...'
    });

    // Muxer les sous-titres SANS réencoder
    await this.execFFmpeg([
      '-i', 'base_video.mp4',
      '-i', 'subtitles.srt',
      '-c', 'copy',              // Copier vidéo et audio sans réencodage
      '-c:s', 'mov_text',        // Encoder les sous-titres en format MP4
      '-metadata:s:s:0', `language=${activeSubtitleLanguage}`,
      'output_with_subs.mp4'
    ], 'muxing sous-titres');

    this.updateProgress({
      phase: 'muxing',
      progress: 95,
      message: 'Finalisation...'
    });

    const outputData = await this.ffmpeg.readFile('output_with_subs.mp4');
    const outputBlob = new Blob([outputData as any], { type: 'video/mp4' });

    await this.safeDelete('base_video.mp4');
    await this.safeDelete('subtitles.srt');
    await this.safeDelete('output_with_subs.mp4');

    console.log('[MuxingExporter] Vidéo avec sous-titres créée:', outputBlob.size, 'bytes');

    return outputBlob;
  }

  /**
   * Générer fichier SRT
   */
  private generateSRT(subtitles: any[]): string {
    return subtitles.map((sub, index) => {
      const startTime = this.formatSRTTime(sub.start);
      const endTime = this.formatSRTTime(sub.end);
      return `${index + 1}\n${startTime} --> ${endTime}\n${sub.text}\n`;
    }).join('\n');
  }

  private formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
  }

  private async ensureSourceFile(clip: Clip): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');
    const url = clip.url!;

    const cached = this.sourceCache.get(url);
    if (cached) return cached;

    // OPTIMIZATION: Use WORKERFS for large files available locally
    // This avoids loading 6GB+ files into RAM via fetch+writeFile
    const fileRef = (clip as any)._file as File | undefined;

    if (fileRef) {
      const uniqueId = Date.now().toString() + Math.random().toString().slice(2, 6);
      const mountDir = `/mnt_export_${uniqueId}`;
      try { await this.ffmpeg.createDir(mountDir); } catch (e) { }

      // FIX: Sanitize filename for WORKERFS mount to avoid path errors
      const ext = fileRef.name.split('.').pop() || 'mp4';
      const safeName = `source_input.${ext}`;
      const safeFile = new File([fileRef], safeName, { type: fileRef.type });

      await this.ffmpeg.mount('WORKERFS' as any, { files: [safeFile] }, mountDir);

      // Note: We need to track mounted dirs to cleanup them later
      // For now, we rely on purgeSources or simple cleanup
      // Since the cache maps URL -> filename, we can map URL -> full mounted path
      const sourceFilename = `${mountDir}/${safeName}`;
      console.log(`[MuxingExporter] Mounted large file at ${sourceFilename}`);

      this.sourceCache.set(url, sourceFilename);
      return sourceFilename;
    }

    // Fallback: fetch and write to MEMFS
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Impossible de télécharger la source vidéo (${response.status})`);
    }

    const sourceBlob = await response.blob();
    const sourceFilename = `source_${this.sourceSequence++}.mp4`;
    await this.ffmpeg.writeFile(sourceFilename, await fetchFile(sourceBlob));

    this.sourceCache.set(url, sourceFilename);
    return sourceFilename;
  }

  private async execFFmpeg(args: string[], context: string): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    this.resetFFmpegLogBuffer();

    try {
      await this.ffmpeg.exec(args);
    } catch (error) {
      if (isLikelyFFmpegOutOfMemoryError(error, this.ffmpegLogs)) {
        throw new FFmpegOutOfMemoryError(
          `FFmpeg est à court de mémoire pendant ${context}`,
          { cause: error, logs: [...this.ffmpegLogs] }
        );
      }
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  private async safeDelete(filename: string): Promise<void> {
    if (!this.ffmpeg || !filename) return;
    try {
      await this.ffmpeg.deleteFile(filename);
    } catch (e) {
      // Ignore delete errors
    }
  }

  private async purgeSources(files?: Set<string>): Promise<void> {
    if (!this.ffmpeg) return;

    const targets = files ?? new Set(this.sourceCache.values());

    for (const filename of targets) {
      await this.safeDelete(filename);
    }

    if (files) {
      for (const [url, filename] of Array.from(this.sourceCache.entries())) {
        if (files.has(filename)) {
          this.sourceCache.delete(url);
        }
      }
    } else {
      this.sourceCache.clear();
    }

    if (this.sourceCache.size === 0) {
      this.sourceSequence = 0;
    }
  }

  private resetFFmpegLogBuffer(): void {
    this.ffmpegLogs = [];
  }

  private recordFFmpegLog(message: string): void {
    this.ffmpegLogs.push(message);
    if (this.ffmpegLogs.length > 200) {
      this.ffmpegLogs.shift();
    }
  }

  private async cleanup(): Promise<void> {
    if (this.ffmpeg && this.isFFmpegLoaded) {
      await this.purgeSources();
      await this.ffmpeg.terminate();
      FFmpegService.markTerminated(); // Notifier le service que l'instance est terminée
      this.ffmpeg = null;
      this.isFFmpegLoaded = false;
      this.resetFFmpegLogBuffer();
    }
  }

  private updateProgress(progress: ExportProgress): void {
    this.progressCallback(progress);
  }
}
